import { Link } from '@inertiajs/react';
import confetti from 'canvas-confetti';
import { Award, PartyPopper, Star, Trophy } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import type { ArcadeGalleryGame } from '@/types';

interface LeaderboardProps {
  games: ArcadeGalleryGame[];
  activeSlug: string;
}

interface WinnerSlugs {
  bestGame: string;
  mostPopular: string;
  honorable: string[];
}

// Winners per stop, keyed by the event slug the gallery is showing. A stop with no
// entry (or whose slugs are not in its gallery) renders no leaderboard.
const WINNER_SLUGS_BY_EVENT: Record<string, WinnerSlugs> = {
  '26-ar': {
    bestGame: 'gravity-duel-final-ten',
    mostPopular: 'paloma-panic-prod-panic',
    honorable: [
      'quantum-tunnel',
      'strike-and-stone',
      'pixel-brawl',
      'obelisco',
      'astro-dash',
    ],
  },
  '26-mx': {
    bestGame: 'luchador',
    mostPopular: 'luchador',
    honorable: [],
  },
  '26-co': {
    bestGame: 'viernes-de-rumba',
    mostPopular: 'san-antonio-drift',
    honorable: [],
  },
};

function getMinifiedSizeKB(codeSize: number) {
  return (codeSize / 1024).toFixed(2);
}

function triggerConfetti() {
  const duration = 3000;
  const animationEnd = Date.now() + duration;

  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      clearInterval(interval);
      return;
    }

    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      colors: ['#e0ff00', '#000000', '#ffffff'],
    });

    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      colors: ['#e0ff00', '#000000', '#ffffff'],
    });
  }, 50);
}

function useGamePreview(game: ArcadeGalleryGame) {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasPreview = !!game.gameplayPreviewUrl;

  function onMouseEnter() {
    setIsHovered(true);
    if (hasPreview && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }

  function onMouseLeave() {
    setIsHovered(false);
    if (hasPreview && videoRef.current) {
      videoRef.current.pause();
    }
  }

  return { isHovered, videoRef, hasPreview, onMouseEnter, onMouseLeave };
}

function GamePreviewMedia({
  game,
  fallbackTextClass,
  fallbackTextSize,
  isHovered,
  hasPreview,
  videoRef,
}: {
  game: ArcadeGalleryGame;
  fallbackTextClass: string;
  fallbackTextSize: string;
  isHovered: boolean;
  hasPreview: boolean;
  videoRef: React.Ref<HTMLVideoElement>;
}) {
  return (
    <>
      {hasPreview && (
        <video
          ref={videoRef}
          src={game.gameplayPreviewUrl ?? ''}
          poster={game.gameplayPosterUrl ?? undefined}
          muted
          loop
          playsInline
          preload="none"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
      {game.coverUrl ? (
        <img
          src={game.coverUrl}
          alt={game.title}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
            hasPreview && isHovered ? 'opacity-0' : 'opacity-100'
          }`}
        />
      ) : (
        <div
          className={`flex h-full w-full items-center justify-center transition-opacity duration-300 ${
            hasPreview && isHovered ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <span
            className={`px-2 text-center font-bold font-sans ${fallbackTextSize} ${fallbackTextClass}`}
          >
            {game.title}
          </span>
        </div>
      )}
    </>
  );
}

function WinnerCard({
  game,
  category,
  voteCount,
}: {
  game: ArcadeGalleryGame;
  // 'both' is one game that took best game and most voted — rendered once, styled
  // as best game, labelled with both.
  category: 'best' | 'popular' | 'both';
  voteCount?: number;
}) {
  const isBest = category !== 'popular';

  const borderClass = isBest
    ? 'border-yellow-400 hover:shadow-[-6px_6px_0px_#facc15]'
    : 'border-pink-400 hover:shadow-[-6px_6px_0px_#f472b6]';
  const bgClass = isBest ? 'bg-yellow-400/10' : 'bg-pink-400/10';
  const textClass = isBest ? 'text-yellow-600' : 'text-pink-600';
  const badgeBg = isBest ? 'bg-yellow-400' : 'bg-pink-400';
  const label =
    category === 'both'
      ? 'MEJOR JUEGO · MÁS VOTADO'
      : isBest
        ? 'MEJOR JUEGO'
        : 'MÁS VOTADO';
  const preview = useGamePreview(game);

  return (
    <Link
      href={`/26/arcade/${game.slug}`}
      onMouseEnter={preview.onMouseEnter}
      onMouseLeave={preview.onMouseLeave}
      className={`group hover:-translate-y-1 relative flex cursor-pointer flex-col gap-3 border-2 bg-background p-4 transition-all hover:translate-x-1 ${borderClass}`}
    >
      <div className="-top-3 -left-3 absolute z-10 flex flex-col gap-1">
        <div
          className={`flex h-10 w-10 items-center justify-center ${badgeBg}`}
        >
          {isBest ? (
            <Trophy className="h-6 w-6 text-black" />
          ) : (
            <Award className="h-6 w-6 text-black" />
          )}
        </div>
        {category === 'both' && (
          <div className="flex h-10 w-10 items-center justify-center bg-pink-400">
            <Award className="h-6 w-6 text-black" />
          </div>
        )}
      </div>

      <div
        className={`relative aspect-[4/3] w-full overflow-hidden ${bgClass}`}
      >
        <GamePreviewMedia
          game={game}
          fallbackTextClass={textClass}
          fallbackTextSize="text-2xl"
          isHovered={preview.isHovered}
          hasPreview={preview.hasPreview}
          videoRef={preview.videoRef}
        />
      </div>

      <div className="flex flex-col gap-1 text-left">
        <div className="flex items-center justify-between gap-2">
          <span className={`font-bold font-sans text-xs ${textClass}`}>
            {label}
          </span>
          {typeof voteCount === 'number' && (
            <span className={`font-bold font-mono text-xs ${textClass}`}>
              {voteCount} votos
            </span>
          )}
        </div>
        <h3 className="line-clamp-2 font-bold font-sans text-primary text-xl">
          {game.title}
        </h3>
        <p className="font-sans text-foreground/70 text-sm">
          by {game.githubUsername}
        </p>
        <p className="font-mono text-foreground/60 text-xs">
          {getMinifiedSizeKB(game.codeSize)} KB
        </p>
      </div>
    </Link>
  );
}

function HonorableCard({ game }: { game: ArcadeGalleryGame }) {
  const preview = useGamePreview(game);
  return (
    <Link
      href={`/26/arcade/${game.slug}`}
      onMouseEnter={preview.onMouseEnter}
      onMouseLeave={preview.onMouseLeave}
      className="group hover:-translate-y-1 relative flex cursor-pointer flex-col gap-2 border-2 border-primary/50 bg-background p-3 transition-all hover:translate-x-1 hover:shadow-[-3px_3px_0px_hsl(var(--primary)/0.5)]"
    >
      <div className="-top-2 -left-2 absolute z-10">
        <div className="flex h-6 w-6 items-center justify-center bg-primary/50">
          <Star className="h-3 w-3 text-background" />
        </div>
      </div>

      <div className="relative aspect-[4/3] w-full overflow-hidden bg-primary/5">
        <GamePreviewMedia
          game={game}
          fallbackTextClass="text-primary/70"
          fallbackTextSize="text-sm"
          isHovered={preview.isHovered}
          hasPreview={preview.hasPreview}
          videoRef={preview.videoRef}
        />
      </div>

      <div className="flex flex-col gap-0.5 text-left">
        <h3 className="line-clamp-1 font-bold font-sans text-primary text-sm">
          {game.title}
        </h3>
        <p className="font-sans text-foreground/60 text-xs">
          by {game.githubUsername}
        </p>
      </div>
    </Link>
  );
}

export function Leaderboard({ games, activeSlug }: LeaderboardProps) {
  const winnerSlugs = WINNER_SLUGS_BY_EVENT[activeSlug];
  if (!winnerSlugs) return null;

  const bestGame = games.find((g) => g.slug === winnerSlugs.bestGame);
  const mostPopular = games.find((g) => g.slug === winnerSlugs.mostPopular);
  const honorableMentions = winnerSlugs.honorable
    .map((slug) => games.find((g) => g.slug === slug))
    .filter((g): g is ArcadeGalleryGame => g !== undefined);

  if (!bestGame && !mostPopular && honorableMentions.length === 0) {
    return null;
  }

  const popularVotes = mostPopular ? mostPopular.voteCount : undefined;

  return (
    <section className="mb-16">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-bold font-sans text-2xl text-primary md:text-3xl">
          <span className="bg-primary px-4 py-2 text-background">
            🏆 leaderboard
          </span>
        </h2>
        <Button
          onClick={triggerConfetti}
          variant="outline"
          className="gap-2 border-2 border-primary"
        >
          <PartyPopper className="h-4 w-4" />
          Celebrate!
        </Button>
      </div>

      <div className="space-y-8">
        {bestGame && bestGame === mostPopular ? (
          <div className="mx-auto w-full max-w-xl">
            <WinnerCard
              game={bestGame}
              category="both"
              voteCount={popularVotes}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {bestGame && <WinnerCard game={bestGame} category="best" />}
            {mostPopular && (
              <WinnerCard
                game={mostPopular}
                category="popular"
                voteCount={popularVotes}
              />
            )}
          </div>
        )}

        {honorableMentions.length > 0 && (
          <div>
            <h3 className="mb-4 font-bold font-sans text-lg text-primary/80">
              Menciones Honoríficas
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
              {honorableMentions.map((game) => (
                <HonorableCard key={game.id} game={game} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div
        className="mx-auto mt-12 flex items-center justify-center gap-3"
        aria-hidden="true"
      >
        <span className="h-px w-16 bg-primary/30 sm:w-24" />
        <span className="h-1.5 w-1.5 rotate-45 bg-primary/40" />
        <span className="h-px w-16 bg-primary/30 sm:w-24" />
      </div>
    </section>
  );
}
